"use client";

import { c as _c } from "react/compiler-runtime";
import { IconFileDownload } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconArrowDownOutline18, IconArrowUpOutline18, IconCheckOutline18, IconChevronLeftOutline18, IconChevronRightOutline18, IconEnvelopeOutline18, IconTrashOutline18, IconWalletOutline18 } from "nucleo-ui-outline-18";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useDetachPaymentMethod, useStripeBillingDetails } from "@/hooks/useStripeBilling";
import { isSafeHttpUrl } from "@/lib/routes";
import toast from "@/lib/toast";
import type { BillingInvoice, BillingPaymentMethod } from "@/types/billing";
import { useCallback, useMemo, useState } from "react";
import { AddPaymentMethodModal } from "./AddPaymentMethodModal";
const INVOICES_PER_PAGE = 7;
// Stagger between each programmatic download to avoid browser throttling.
const DOWNLOAD_STAGGER_MS = 150;

// ---------------------------------------------------------------------------
// Brand marks — inline SVG stand-ins. The Figma references the official Visa
// and Mastercard assets; until the assets package is imported we render
// recognisable substitutes that stay within the 46x32 tile dimensions.
// ---------------------------------------------------------------------------

function VisaMark() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 46 32" className="h-full w-full" aria-label="Visa" role="img"><text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontFamily="'Geist', sans-serif" fontWeight="700" fontSize="13" letterSpacing="0.5" fill="#1A1F71">VISA</text></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function MastercardMark() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 46 32" className="h-full w-full" aria-label="Mastercard" role="img"><circle cx="19" cy="16" r="8" fill="#EB001B" /><circle cx="27" cy="16" r="8" fill="#F79E1B" /><path d="M23 10a8 8 0 0 0 0 12 8 8 0 0 0 0-12Z" fill="#FF5F00" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function CardBrandTile(t0) {
  const $ = _c(5);
  const {
    brand
  } = t0;
  const t1 = brand ?? "";
  let t2;
  if ($[0] !== t1) {
    t2 = t1.toLowerCase();
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const normalized = t2;
  let t3;
  if ($[2] !== brand || $[3] !== normalized) {
    t3 = <div className="border-ds-border-default bg-ds-surface-card flex h-8 w-[46px] shrink-0 items-center justify-center rounded-md border p-1">{normalized === "visa" ? <VisaMark /> : normalized === "mastercard" ? <MastercardMark /> : <span className="text-ds-text-muted text-3xs font-bold uppercase">{brand ?? "Card"}</span>}</div>;
    $[2] = brand;
    $[3] = normalized;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

// ---------------------------------------------------------------------------
// Sub-sections
// ---------------------------------------------------------------------------

function PaymentMethodRow(t0) {
  const $ = _c(30);
  const {
    method,
    onDelete,
    isDeleting
  } = t0;
  const brandLabel = method.brand ? method.brand.charAt(0).toUpperCase() + method.brand.slice(1) : "Card";
  const expiry = method.expMonth && method.expYear ? `${String(method.expMonth).padStart(2, "0")}/${method.expYear}` : "N/A";
  let t1;
  if ($[0] !== method.brand) {
    t1 = <CardBrandTile brand={method.brand} />;
    $[0] = method.brand;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = method.last4 ?? "****";
  let t3;
  if ($[2] !== brandLabel || $[3] !== t2) {
    t3 = <p className="body-sm-medium">{brandLabel} ending in {t2}</p>;
    $[2] = brandLabel;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== expiry) {
    t4 = <p className="body-sm">Expiry {expiry}</p>;
    $[5] = expiry;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <div className="min-w-0 flex-1">{t3}{t4}</div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== method.id || $[11] !== onDelete) {
    t6 = () => onDelete(method.id);
    $[10] = method.id;
    $[11] = onDelete;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = `Remove ${brandLabel} ending in ${method.last4 ?? "****"}`;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <IconTrashOutline18 className="h-4 w-4" aria-hidden="true" />;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = isDeleting ? "Removing..." : "Remove";
  let t10;
  if ($[14] !== isDeleting || $[15] !== t6 || $[16] !== t7 || $[17] !== t9) {
    t10 = <Button variant="white" size="sm" onClick={t6} disabled={isDeleting} aria-label={t7}>{t8}{t9}</Button>;
    $[14] = isDeleting;
    $[15] = t6;
    $[16] = t7;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t5) {
    t11 = <div className="flex items-start gap-3">{t5}{t10}</div>;
    $[19] = t10;
    $[20] = t5;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== method.billingEmail) {
    t12 = method.billingEmail ? <div className="flex items-center gap-1.5"><IconEnvelopeOutline18 className="text-ds-text-muted h-4 w-4" aria-hidden="true" /><span className="body-sm">{method.billingEmail}</span></div> : null;
    $[22] = method.billingEmail;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t11 || $[25] !== t12) {
    t13 = <div className="flex min-w-0 flex-1 flex-col gap-2">{t11}{t12}</div>;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t1 || $[28] !== t13) {
    t14 = <div className="border-ds-border-default bg-ds-surface-card flex items-start gap-3 rounded-lg border p-4">{t1}{t13}</div>;
    $[27] = t1;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
}
function PaymentMethodSection(t0) {
  const $ = _c(12);
  const {
    methods,
    onDelete,
    deletingId,
    onAdd,
    isLoading
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="min-w-0 flex-1"><h2 className="body-semibold">Payment method</h2><p className="body-sm mt-0.5">Change how you pay for your plan.</p></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <IconWalletOutline18 className="h-4 w-4" aria-hidden="true" />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onAdd) {
    t3 = <div className="mb-5 flex items-start justify-between gap-5">{t1}<Button variant="white" size="sm" onClick={onAdd}>{t2}New Payment Method</Button></div>;
    $[2] = onAdd;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== deletingId || $[5] !== isLoading || $[6] !== methods || $[7] !== onDelete) {
    t4 = isLoading && methods.length === 0 ? <p className="body-sm text-ds-text-muted py-8 text-center">Loading payment methods...</p> : methods.length === 0 ? <p className="body-sm text-ds-text-muted py-8 text-center">No saved cards — a payment method appears after the first Stripe checkout.</p> : <div className="space-y-5">{methods.map(method => <PaymentMethodRow key={method.id} method={method} onDelete={onDelete} isDeleting={deletingId === method.id} />)}</div>;
    $[4] = deletingId;
    $[5] = isLoading;
    $[6] = methods;
    $[7] = onDelete;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <section className="border-ds-border-default bg-ds-surface-card shadow-ds-soft rounded-xl border p-6">{t3}{t4}</section>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function PaidBadge() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 py-0.5 pl-1.5 pr-2 text-xs font-medium text-green-700 dark:border-green-900/40 dark:bg-green-900/20 dark:text-green-400"><IconCheckOutline18 className="h-3 w-3" aria-hidden="true" />Paid</span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function formatCurrency(amountCents: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "usd").toUpperCase()
    }).format(amountCents / 100);
  } catch {
    return `$${(amountCents / 100).toFixed(2)}`;
  }
}
function BillingInvoicesSection({
  invoices,
  pageSize = INVOICES_PER_PAGE,
  onDownloadAll,
  isLoading,
  isDownloadingAll
}: {
  invoices: BillingInvoice[];
  pageSize?: number;
  onDownloadAll: () => void;
  isLoading: boolean;
  isDownloadingAll: boolean;
}) {
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(invoices.length / pageSize));
  const clampedPage = Math.min(page, totalPages - 1);
  const pageStart = clampedPage * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, invoices.length);
  const pageInvoices = useMemo(() => invoices.slice(pageStart, pageEnd), [invoices, pageStart, pageEnd]);
  const firstIndex = invoices.length === 0 ? 0 : pageStart + 1;
  return <section className="border-ds-border-default bg-ds-surface-card shadow-ds-soft rounded-xl border">
      <div className="flex items-start justify-between gap-5 p-6 pb-5">
        <div className="min-w-0 flex-1">
          <h2 className="body-semibold">Billing and invoicing</h2>
          <p className="body-sm mt-0.5">Manage your billing and payment details.</p>
        </div>
        <Button variant="white" size="sm" onClick={onDownloadAll} disabled={isDownloadingAll}>
          <IconFileDownload className="h-4 w-4" aria-hidden="true" />
          {isDownloadingAll ? "Downloading..." : "Download all"}
        </Button>
      </div>
      <div className="border-ds-border-default overflow-hidden border-t">
        {isLoading && invoices.length === 0 ? <p className="body-sm text-ds-text-muted py-8 text-center">Loading invoices...</p> : invoices.length === 0 ? <p className="body-sm text-ds-text-muted py-8 text-center">No invoices yet.</p> : <Table>
            <TableHeader>
              <TableRow className="border-ds-border-default bg-ds-surface-card hover:bg-ds-surface-hover border-b">
                <TableHead className="w-12 px-6">
                  <input type="checkbox" disabled aria-label="Select all invoices (not yet implemented)" className="accent-ds-brand-500 border-ds-neutral-300 dark:border-ds-neutral-700 h-4 w-4 cursor-not-allowed rounded border opacity-60" />
                </TableHead>
                <TableHead>
                  <SortableHeaderLabel label="Invoice" />
                </TableHead>
                <TableHead>
                  <SortableHeaderLabel label="Billing date" direction="desc" />
                </TableHead>
                <TableHead>
                  <SortableHeaderLabel label="Status" />
                </TableHead>
                <TableHead>
                  <SortableHeaderLabel label="Amount" />
                </TableHead>
                <TableHead>
                  <SortableHeaderLabel label="Period" />
                </TableHead>
                <TableHead className="text-right">&nbsp;</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageInvoices.map(invoice => <TableRow key={invoice.id} className="border-ds-border-default dark:hover:bg-ds-neutral-800/40 hover:bg-ds-surface-hover transition-colors">
                  <TableCell className="w-12 px-6">
                    <input type="checkbox" disabled aria-label={`Select invoice ${invoice.number ?? invoice.id} (not yet implemented)`} className="accent-ds-brand-500 border-ds-neutral-300 dark:border-ds-neutral-700 h-4 w-4 cursor-not-allowed rounded border opacity-60" />
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex items-center gap-3">
                      <PdfThumbnail />
                      <span className="text-ds-text-primary text-sm font-medium">{invoice.number ?? invoice.id}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-ds-text-subtle dark:text-ds-text-disabled py-4 text-sm">
                    {invoice.createdAt ? new Date(invoice.createdAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                timeZone: "UTC"
              }) : "\u2014"}
                  </TableCell>
                  <TableCell className="py-4">
                    {invoice.status === "paid" ? <PaidBadge /> : <span className="text-ds-text-muted text-xs capitalize">{invoice.status}</span>}
                  </TableCell>
                  <TableCell className="text-ds-text-subtle dark:text-ds-text-disabled py-4 text-sm">
                    {formatCurrency(invoice.status === "paid" ? invoice.amountPaid : invoice.total, invoice.currency)}
                  </TableCell>
                  <TableCell className="text-ds-text-subtle dark:text-ds-text-disabled py-4 text-sm">
                    {invoice.periodStart ? new Date(invoice.periodStart).toLocaleDateString("en-US", {
                month: "short",
                year: "numeric",
                timeZone: "UTC"
              }) : "\u2014"}
                  </TableCell>
                  <TableCell className="py-4 pr-6 text-right">
                    {isSafeHttpUrl(invoice.invoicePdf) ? <a href={invoice.invoicePdf} target="_blank" rel="noopener noreferrer" className="text-ds-brand-500 hover:text-ds-brand-600 dark:text-ds-brand-400 text-sm font-medium">
                        Download
                      </a> : null}
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>}
      </div>
      {invoices.length > pageSize ? <div className="border-ds-border-default flex items-center justify-between gap-3 border-t px-6 py-4">
          <p className="text-ds-text-muted text-sm">
            Showing {firstIndex}-{pageEnd} of {invoices.length} invoices
          </p>
          <nav className="flex gap-2" aria-label="Invoice pagination">
            <Button variant="white" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={clampedPage === 0} aria-label="Previous page">
              <IconChevronLeftOutline18 className="h-4 w-4" aria-hidden="true" />
              <span className="text-xs">Previous</span>
            </Button>
            <Button variant="white" size="sm" onClick={() => setPage(p_0 => Math.min(totalPages - 1, p_0 + 1))} disabled={clampedPage >= totalPages - 1} aria-label="Next page">
              <span className="text-xs">Next</span>
              <IconChevronRightOutline18 className="h-4 w-4" aria-hidden="true" />
            </Button>
          </nav>
        </div> : null}
    </section>;
}
function SortableHeaderLabel(t0) {
  const $ = _c(5);
  const {
    label,
    direction
  } = t0;
  let t1;
  if ($[0] !== direction) {
    t1 = direction === "asc" ? <IconArrowUpOutline18 className="text-ds-text-muted h-3.5 w-3.5" aria-hidden="true" /> : direction === "desc" ? <IconArrowDownOutline18 className="text-ds-text-muted h-3.5 w-3.5" aria-hidden="true" /> : <UnsortedIcon />;
    $[0] = direction;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label || $[3] !== t1) {
    t2 = <span className="text-ds-text-subtle inline-flex items-center gap-1.5 text-sm font-medium">{label}{t1}</span>;
    $[2] = label;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

// Stacked up/down chevrons used on sortable-but-not-active columns (Figma:
// every sortable header carries this indicator until the user commits a
// direction, at which point it flips to a single arrow).
function UnsortedIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 10 12" className="text-ds-text-disabled h-3 w-2.5" aria-hidden="true" fill="none"><path d="M5 0.5 L8 4 L2 4 Z" fill="currentColor" /><path d="M5 11.5 L8 8 L2 8 Z" fill="currentColor" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function PdfThumbnail() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 28 32" className="dark:text-ds-text-primary h-8 w-7 shrink-0 text-white" aria-hidden="true" fill="none"><path d="M4 2a2 2 0 0 1 2-2h12l8 8v20a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2Z" fill="currentColor" className="stroke-ds-neutral-200 dark:stroke-ds-neutral-700" strokeWidth="1" /><path d="M18 0v6a2 2 0 0 0 2 2h6" className="fill-ds-neutral-100 stroke-ds-neutral-200 dark:fill-ds-neutral-800 dark:stroke-ds-neutral-700" strokeWidth="1" /><rect x="4" y="16" width="18" height="8" rx="1.5" fill="#ef4444" /><text x="13" y="22" textAnchor="middle" fontFamily="'Geist', sans-serif" fontWeight="700" fontSize="6" fill="#ffffff" letterSpacing="0.3">PDF</text></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

// Named export only — `BillingClient.tsx` imports `{ SubscriptionTab }`.
export function SubscriptionTab() {
  const {
    selectedOrgId
  } = useOrganizations();
  const billingQuery = useStripeBillingDetails(selectedOrgId ?? undefined);
  const detachMutation = useDetachPaymentMethod(selectedOrgId ?? "");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deletingPmId, setDeletingPmId] = useState<string | null>(null);
  const paymentMethods = useMemo(() => billingQuery.data?.paymentMethods ?? [], [billingQuery.data?.paymentMethods]);
  const invoices = useMemo(() => billingQuery.data?.invoices ?? [], [billingQuery.data?.invoices]);
  const handleAddPaymentMethod = useCallback(() => {
    setAddModalOpen(true);
  }, []);
  const handleDeletePaymentMethod = useCallback(async (pmId: string) => {
    if (!confirm("Remove this payment method?")) return;
    setDeletingPmId(pmId);
    try {
      await detachMutation.mutateAsync(pmId);
      toast.success("Payment method removed.");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to remove payment method.";
      toast.error(message);
    } finally {
      setDeletingPmId(null);
    }
  }, [detachMutation.mutateAsync]);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const handleDownloadAll = useCallback(async () => {
    const downloadable = invoices.filter(inv => isSafeHttpUrl(inv.invoicePdf));
    if (downloadable.length === 0) {
      toast.info("No invoices to download.");
      return;
    }
    setIsDownloadingAll(true);
    let downloaded = 0;
    let failed = 0;
    for (const inv_0 of downloadable) {
      const pdfUrl = inv_0.invoicePdf as string; // safe — filtered by isSafeHttpUrl above
      const filename = `${inv_0.number ?? inv_0.id}.pdf`;
      let blobUrl: string | undefined;
      try {
        // Stripe PDF URLs block client-side fetch (no CORS headers),
        // so we proxy through our own server-side route.
        const proxyUrl = `/api/invoice-pdf?url=${encodeURIComponent(pdfUrl)}&filename=${encodeURIComponent(filename)}`;
        const res = await fetch(proxyUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        downloaded++;
      } catch {
        failed++;
      } finally {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
      }
      // Stagger between downloads to avoid browser throttling.
      await new Promise<void>(r => setTimeout(r, DOWNLOAD_STAGGER_MS));
    }
    setIsDownloadingAll(false);
    if (failed > 0 && downloaded > 0) {
      toast.warning(`Downloaded ${downloaded} of ${downloadable.length} invoices. ${failed} failed — try downloading individually.`);
    } else if (failed > 0 && downloaded === 0) {
      toast.error("Could not download invoices. Please download them individually.");
    } else {
      toast.success(`Downloaded ${downloaded} invoice${downloaded > 1 ? "s" : ""}.`);
    }
  }, [invoices]);
  return <div className="space-y-6">
      <PaymentMethodSection methods={paymentMethods} onDelete={handleDeletePaymentMethod} deletingId={deletingPmId} onAdd={handleAddPaymentMethod} isLoading={billingQuery.isLoading} />
      <BillingInvoicesSection invoices={invoices} onDownloadAll={handleDownloadAll} isLoading={billingQuery.isLoading} isDownloadingAll={isDownloadingAll} />
      <AddPaymentMethodModal open={addModalOpen} onOpenChange={setAddModalOpen} />
    </div>;
}